import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './DashboardHeader.module.scss'
import { Plus } from 'lucide-react'
import {
  getDesktopSuggestions,
  getMobileResponses,
  getPostFlowSuggestions,
  normalizeText,
  type DesktopSuggestion,
  type MobileResponse
} from './dashboardActionsConfig.tsx'
import { withConversationState } from '../../../utils/navigationHelpers.ts'
import useNavVisibility from '../../../hooks/useNavVisibility.ts'
import { useDispatch } from 'react-redux'
import { useConversationalAgent } from '../../../contexts/ConversationalAgentContext.tsx'
import ConversationalMode from './ConversationalMode/ConversationalMode.tsx'
import ConversationInput from './ConversationalMode/ConversationInput/ConversationInput.tsx'
import { registerTutorialRef } from '../../../utils/tutorialRefsRegistry.ts'
import { AGENT_NAMES } from '../../../constants/agentNames.ts'
import {
  isGeneralAgent,
  isPostSpecialistMediaContext,
  isProjectAutofillPdfContext,
  type PostMediaConfirmRoute
} from '../../../utils/conversationalMode/composerDocumentChoice.ts'
import { pendingAttachmentFallbackCaption } from '../../../utils/agentImageStorage.ts'
import {
  fileTooLargeUserMessage,
  isPdfLikeAttachment,
  CHATBOT_PROJECT_AUTOFILL_DOCUMENTATION_MAX_FILES,
  PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES
} from '../../../services/llmService.ts'

const MOBILE_PRIORITY_IDS = ['cotizar-materiales', 'ofrecer-servicios', 'publicar-oferta-trabajo']
const ANALIZAR_DOCS_MOBILE_SUGGESTION_ID = 'analizar-docs-obra-cotizaciones'
const MOBILE_PRIORITY_ROTATIONS: string[][] = [
  ['cotizar-materiales', 'ofrecer-servicios', 'publicar-oferta-trabajo'],
  ['ofrecer-servicios', 'publicar-oferta-trabajo', 'cotizar-materiales'],
  ['publicar-oferta-trabajo', 'cotizar-materiales', 'ofrecer-servicios'],
]
const SUBTITLE_PHRASES = [
  '¿En qué podemos ayudarte hoy?',
  '¿Querés cotizar materiales?',
  '¿Buscás profesionales o contratistas?',
  '¿Necesitás publicar una vacante?',
  '¿Querés publicar un servicio o producto?',
  '¿Querés cotizar tu obra con tu documentación?',
  '¿Buscás licitaciones públicas?',
  '¿Querés ver cotizaciones disponibles?',
]

const SESSION_CHIPS_BUCKET_KEY = 'edify_mobile_chips_bucket'

const getSessionChipsBucket = (userId?: string): number => {
  if (typeof window === 'undefined') return 0
  try {
    const key = `${SESSION_CHIPS_BUCKET_KEY}_${userId ?? 'anon'}`
    const stored = sessionStorage.getItem(key)
    if (stored !== null) {
      const n = parseInt(stored, 10)
      if (n >= 0 && n < MOBILE_PRIORITY_ROTATIONS.length) return n
    }
    const bucket = Math.floor(Math.random() * MOBILE_PRIORITY_ROTATIONS.length)
    sessionStorage.setItem(key, String(bucket))
    return bucket
  } catch {
    return 0
  }
}

export interface MobileResultsPayload {
  status: 'idle' | 'loading' | 'result' | 'empty'
  label?: string
  summary?: string
  cta?: () => void
}

interface DashboardHeaderProps {
  userName: string
  userId?: string
  userData?: any
  onMobileResultsChange?: (payload: MobileResultsPayload) => void
  onConversationalModeChange?: (isActive: boolean) => void
  onAgentStatusChange?: (isProcessing: boolean) => void
  onConversationalActionsReady?: (actions: {
    onCreateNewSession: () => void
    onOpenSearchModal: () => void
    onCloseConversationalMode: () => void
    hasActiveConversation?: boolean
    totalChatsCount?: number
  }) => void
  onSidebarStateChange?: (isCollapsed: boolean) => void
  sidebarCollapsed?: boolean
  mobileActionsPlaceholder?: React.ReactNode
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  userName,
  userId,
  userData,
  onMobileResultsChange,
  onConversationalModeChange,
  onAgentStatusChange,
  onConversationalActionsReady,
  onSidebarStateChange,
  sidebarCollapsed = false,
  mobileActionsPlaceholder
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDraftHandoff, setSearchDraftHandoff] = useState<string | null>(null)
  const [welcomeStagedFile, setWelcomeStagedFile] = useState<File | null>(null)
  const [welcomeStagedExtraPdfFiles, setWelcomeStagedExtraPdfFiles] = useState<File[]>([])
  const [welcomePdfChoicePending, setWelcomePdfChoicePending] = useState(false)
  const [welcomePostMediaChoicePending, setWelcomePostMediaChoicePending] = useState(false)
  const [welcomeAttachmentError, setWelcomeAttachmentError] = useState<string | null>(null)
  const [welcomePreviewUrl, setWelcomePreviewUrl] = useState<string | null>(null)
  const welcomeFormRef = useRef<HTMLFormElement>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [matchedSuggestion, setMatchedSuggestion] = useState<DesktopSuggestion | null>(null)
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [subtitleDisplayText, setSubtitleDisplayText] = useState('')
  const [subtitlePhraseIndex, setSubtitlePhraseIndex] = useState(0)
  const [isDeletingSubtitle, setIsDeletingSubtitle] = useState(false)
  const [isHoveringSubtitle, setIsHoveringSubtitle] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<any>()
  const conversationalShortcutHandledKeyRef = useRef<string | null>(null)

  const {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    removeSession,
    clearCurrentSession,
    isConversationalModeActive,
    activateConversationalMode,
    deactivateConversationalMode,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    activeAgent,
    collectedData,
    sendMessage,
    isProcessing: contextIsProcessing,
    missingFields,
    readyToExecute,
    hasPendingImage,
    imageUploadAcknowledged,
    pendingImagePreview,
    pendingAttachmentMime,
    pendingAttachmentFileName,
    pendingDocumentationFileCount,
    pendingDocumentationNamesSummary,
    pendingAttachmentSizeBytes,
    lastAnalyzedDocumentationFileName,
    documentPipelineLoopActive,
    documentPipelinePhase,
    documentPipelineStreamHint,
    documentPipelinePrimaryFileOriginalName,
    documentPipelinePrimaryFileSizeBytes,
    storePendingImage,
    clearPendingImage,
    beginComposerPreserveForSession,
    endComposerPreserveForSession,
    openChatHistoryModal,
    composerDocumentChoicePending,
    composerPostMediaChoicePending,
    confirmPendingDocumentAsProjectPreload,
    signalProjectAutofillReadyToSendHint,
    showProjectAutofillSendHint,
    signalPostMediaReadyToSendHint,
    showPostMediaSendHint,
    setActiveAgent,
    setCollectedData
  } = useConversationalAgent()

  const welcomeUsesContextPending = useMemo(
    () =>
      !isConversationalModeActive &&
      hasPendingImage &&
      activeAgent === AGENT_NAMES.projectAutofillAgent &&
      collectedData?.intent === 'project_autofill',
    [
      isConversationalModeActive,
      hasPendingImage,
      activeAgent,
      collectedData?.intent
    ]
  )

  const welcomeUsesContextPost = useMemo(
    () =>
      !isConversationalModeActive &&
      hasPendingImage &&
      activeAgent === AGENT_NAMES.postAgent &&
      (collectedData?.intent === 'create_post' ||
        collectedData?.intent === 'create_portfolio_item'),
    [
      isConversationalModeActive,
      hasPendingImage,
      activeAgent,
      collectedData?.intent
    ]
  )

  const welcomeAllowVideo =
    collectedData?.intent === 'create_post' ||
    welcomePostMediaChoicePending ||
    activeAgent === AGENT_NAMES.postAgent ||
    activeAgent === AGENT_NAMES.general
  const welcomeAllowDocuments =
    activeAgent === AGENT_NAMES.general ||
    activeAgent === AGENT_NAMES.projectAutofillAgent ||
    collectedData?.intent === 'project_autofill'
  const welcomeShowAttachment =
    activeAgent === AGENT_NAMES.general ||
    activeAgent === AGENT_NAMES.projectAutofillAgent ||
    activeAgent === AGENT_NAMES.postAgent ||
    collectedData?.intent === 'project_autofill' ||
    collectedData?.intent === 'create_post' ||
    collectedData?.intent === 'create_portfolio_item' ||
    missingFields.includes('image_uploaded') ||
    (readyToExecute &&
      (collectedData?.intent === 'create_post' ||
        collectedData?.intent === 'create_portfolio_item'))
  const consumeSearchDraftHandoff = useCallback(() => {
    setSearchDraftHandoff(null)
  }, [])

  const clearWelcomeStagedAttachment = useCallback(() => {
    setWelcomeStagedFile(null)
    setWelcomeStagedExtraPdfFiles([])
    setWelcomePdfChoicePending(false)
    setWelcomePostMediaChoicePending(false)
    setWelcomeAttachmentError(null)
  }, [])

  const handleWelcomeAttachment = useCallback(
    (filesIn: File[]) => {
      if (!welcomeShowAttachment || isProcessing || contextIsProcessing) return
      setWelcomeAttachmentError(null)
      const picked = filesIn.filter(Boolean)
      if (!picked.length) return
      let batch = picked
      const allPdf = batch.every((f) => isPdfLikeAttachment(f))
      const autofillPdf = isProjectAutofillPdfContext(activeAgent, collectedData?.intent)
      if (batch.length > 1 && !allPdf && !autofillPdf) {
        batch = [batch[0]!]
      }
      if (batch.length > CHATBOT_PROJECT_AUTOFILL_DOCUMENTATION_MAX_FILES) {
        batch = batch.slice(0, CHATBOT_PROJECT_AUTOFILL_DOCUMENTATION_MAX_FILES)
      }
      const file = batch[0]!
      const documentationTail = batch.length > 1 ? batch.slice(1) : []
      const isVideo = file.type.startsWith('video/')
      const isImage = file.type.startsWith('image/')
      const isPdf = isPdfLikeAttachment(file)
      if (
        isVideo &&
        collectedData?.intent !== 'create_post' &&
        !isGeneralAgent(activeAgent) &&
        activeAgent !== AGENT_NAMES.postAgent
      ) {
        return
      }
      const tooBig = batch.find((f) => f.size > PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES)
      if (tooBig) {
        setWelcomeAttachmentError(
          fileTooLargeUserMessage(PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES)
        )
        return
      }
      if (isPdf && !autofillPdf && !isGeneralAgent(activeAgent)) return
      if (isPdf && !autofillPdf && isGeneralAgent(activeAgent)) {
        setWelcomeStagedFile(file)
        setWelcomeStagedExtraPdfFiles(allPdf ? documentationTail : [])
        setWelcomePdfChoicePending(true)
        setWelcomePostMediaChoicePending(false)
        return
      }
      if (
        isGeneralAgent(activeAgent) &&
        (isImage || isVideo) &&
        !isPdf &&
        !isPostSpecialistMediaContext(activeAgent, collectedData?.intent)
      ) {
        setWelcomeStagedFile(file)
        setWelcomeStagedExtraPdfFiles([])
        setWelcomePostMediaChoicePending(true)
        setWelcomePdfChoicePending(false)
        return
      }
      setWelcomePdfChoicePending(false)
      setWelcomePostMediaChoicePending(false)
      setWelcomeStagedFile(file)
      setWelcomeStagedExtraPdfFiles(autofillPdf && batch.length > 1 ? documentationTail : [])
    },
    [
      welcomeShowAttachment,
      isProcessing,
      contextIsProcessing,
      collectedData?.intent,
      activeAgent
    ]
  )

  const handleWelcomeConfirmPreload = useCallback(async () => {
    const file = welcomeStagedFile
    if (!file || !welcomePdfChoicePending) return
    const draft = searchQuery.trim()
    setSearchDraftHandoff(draft.length > 0 ? draft : null)
    setSearchQuery('')
    setWelcomePdfChoicePending(false)
    setWelcomePostMediaChoicePending(false)
    setWelcomeStagedFile(null)
    setWelcomeStagedExtraPdfFiles([])
    setActiveAgent(AGENT_NAMES.projectAutofillAgent)
    setCollectedData((prev) => ({ ...(prev || {}), intent: 'project_autofill' }))
    const newSession = createSession()
    beginComposerPreserveForSession(newSession.sessionId)
    try {
      const payload =
        welcomeStagedExtraPdfFiles.length > 0 ? [file, ...welcomeStagedExtraPdfFiles] : file
      await storePendingImage(payload, {
        sessionAnchor: newSession.sessionId,
        projectAutofillPdfConfirmed: true
      })
      signalProjectAutofillReadyToSendHint()
    } finally {
      endComposerPreserveForSession()
    }
  }, [
    welcomeStagedFile,
    welcomeStagedExtraPdfFiles,
    welcomePdfChoicePending,
    searchQuery,
    setActiveAgent,
    setCollectedData,
    createSession,
    storePendingImage,
    signalProjectAutofillReadyToSendHint,
    beginComposerPreserveForSession,
    endComposerPreserveForSession
  ])

  const handleWelcomeConfirmPostRoute = useCallback(
    async (route: PostMediaConfirmRoute) => {
      const file = welcomeStagedFile
      if (!file || !welcomePostMediaChoicePending) return
      if ((route === 'producto' || route === 'servicio') && file.type.startsWith('video/')) {
        return
      }
      const draft = searchQuery.trim()
      setSearchDraftHandoff(draft.length > 0 ? draft : null)
      setSearchQuery('')
      setWelcomePostMediaChoicePending(false)
      setWelcomeStagedFile(null)
      setWelcomeStagedExtraPdfFiles([])
      setActiveAgent(AGENT_NAMES.postAgent)
      if (route === 'post') {
        setCollectedData((prev) => ({ ...(prev || {}), intent: 'create_post' }))
      } else {
        setCollectedData((prev) => ({
          ...(prev || {}),
          intent: 'create_portfolio_item',
          type: route === 'producto' ? 'producto' : 'servicio'
        }))
      }
      const newSession = createSession()
      beginComposerPreserveForSession(newSession.sessionId)
      try {
        await storePendingImage(file, {
          sessionAnchor: newSession.sessionId,
          postMediaRouting: route
        })
        signalPostMediaReadyToSendHint()
      } finally {
        endComposerPreserveForSession()
      }
    },
    [
      welcomeStagedFile,
      welcomePostMediaChoicePending,
      searchQuery,
      setActiveAgent,
      setCollectedData,
      createSession,
      storePendingImage,
      signalPostMediaReadyToSendHint,
      beginComposerPreserveForSession,
      endComposerPreserveForSession
    ]
  )

  useEffect(() => {
    if (!welcomeStagedFile) {
      setWelcomePreviewUrl(null)
      return
    }
    const t = welcomeStagedFile.type?.trim() || ''
    if (t.startsWith('image/') || t.startsWith('video/')) {
      const u = URL.createObjectURL(welcomeStagedFile)
      setWelcomePreviewUrl(u)
      return () => {
        URL.revokeObjectURL(u)
      }
    }
    setWelcomePreviewUrl(null)
  }, [welcomeStagedFile])

  const welcomePendingAttachmentFileName = useMemo(() => {
    if (welcomeStagedFile == null) return pendingAttachmentFileName
    return welcomeStagedFile.name
  }, [welcomeStagedFile, pendingAttachmentFileName])

  const welcomePendingDocumentationCount = useMemo(
    () => (welcomeStagedFile ? 1 + welcomeStagedExtraPdfFiles.length : 0),
    [welcomeStagedFile, welcomeStagedExtraPdfFiles]
  )

  const welcomePendingDocumentationNamesSummary = useMemo(() => {
    if (!welcomeStagedFile) return null
    return [welcomeStagedFile.name, ...welcomeStagedExtraPdfFiles.map((f) => f.name)].join(', ')
  }, [welcomeStagedFile, welcomeStagedExtraPdfFiles])

  const welcomePendingAttachmentSizeBytes = useMemo(() => {
    if (welcomeStagedFile == null) return pendingAttachmentSizeBytes
    const extra = welcomeStagedExtraPdfFiles.reduce((a, f) => {
      if (typeof f.size === 'number' && Number.isFinite(f.size)) return a + f.size
      return a
    }, 0)
    const primary =
      typeof welcomeStagedFile.size === 'number' && Number.isFinite(welcomeStagedFile.size)
        ? welcomeStagedFile.size
        : 0
    const t = primary + extra
    return t > 0 ? t : pendingAttachmentSizeBytes
  }, [welcomeStagedFile, welcomeStagedExtraPdfFiles, pendingAttachmentSizeBytes])

  const { ocultarEnMobile } = useNavVisibility()
  const isMobile = ocultarEnMobile
  const historyCount = allSessions.length
  const mobileSuggestionsRef = useRef<HTMLDivElement | null>(null)
  const navigateFromConversation = useMemo(() => withConversationState(navigate), [navigate])
  const desktopSuggestions = useMemo(() => {
    const base = getDesktopSuggestions(navigateFromConversation, userId, userData, dispatch)
    const postFlow = getPostFlowSuggestions(() => sendMessage('', { confirmExecution: true }))
    return [...base, ...postFlow]
  }, [navigateFromConversation, userId, userData, dispatch, sendMessage])
  const mobileResponses = useMemo(() => getMobileResponses(navigateFromConversation, userId, userData, dispatch), [navigateFromConversation, userId, userData, dispatch])
  const mobileQuickSuggestions = useMemo(() => {
    const docAnalysis = mobileResponses.find(s => s.id === ANALIZAR_DOCS_MOBILE_SUGGESTION_ID)
    const rotation = MOBILE_PRIORITY_ROTATIONS[getSessionChipsBucket(userId)]
    const prioritized = rotation
      .map(id => mobileResponses.find(suggestion => suggestion.id === id))
      .filter((suggestion): suggestion is MobileResponse => Boolean(suggestion))
    const remaining = mobileResponses.filter(
      suggestion =>
        !MOBILE_PRIORITY_IDS.includes(suggestion.id) && suggestion.id !== ANALIZAR_DOCS_MOBILE_SUGGESTION_ID
    )
    const head = docAnalysis ? [docAnalysis] : []
    return [...head, ...prioritized, ...remaining].slice(0, 8)
  }, [mobileResponses, userId])
  useEffect(() => {
    if (!isMobile) {
      setShowMobileSuggestions(false)
      setShowLeftFade(false)
      return
    }

    const timeout = window.setTimeout(() => {
      setShowMobileSuggestions(true)
    }, 800)

    return () => window.clearTimeout(timeout)
  }, [isMobile])

  useEffect(() => {
    if (!showMobileSuggestions) {
      return
    }

    const element = mobileSuggestionsRef.current
    if (!element) {
      return
    }

    const updateFade = () => {
      setShowLeftFade(element.scrollLeft > 4)
    }

    updateFade()
    element.addEventListener('scroll', updateFade, { passive: true })

    return () => {
      element.removeEventListener('scroll', updateFade)
    }
  }, [showMobileSuggestions])

  useEffect(() => {
    if (
      isFocused ||
      searchQuery ||
      isProcessing ||
      welcomeStagedFile ||
      welcomePostMediaChoicePending ||
      welcomeUsesContextPending ||
      welcomeUsesContextPost
    ) {
      return
    }

    const currentText = SUBTITLE_PHRASES[subtitlePhraseIndex % SUBTITLE_PHRASES.length]
    let timeoutId = 0

    if (!isDeletingSubtitle) {
      if (subtitleDisplayText.length < currentText.length) {
        timeoutId = window.setTimeout(() => {
          setSubtitleDisplayText(currentText.slice(0, subtitleDisplayText.length + 1))
        }, 45)
      } else if (!isHoveringSubtitle) {
        timeoutId = window.setTimeout(() => {
          setIsDeletingSubtitle(true)
        }, 1800)
      }
    } else {
      if (subtitleDisplayText.length > 0) {
        timeoutId = window.setTimeout(() => {
          setSubtitleDisplayText(currentText.slice(0, subtitleDisplayText.length - 1))
        }, 28)
      } else if (isHoveringSubtitle) {
        setSubtitleDisplayText(currentText)
        setIsDeletingSubtitle(false)
      } else {
        setIsDeletingSubtitle(false)
        setSubtitlePhraseIndex((prev) => (prev + 1) % SUBTITLE_PHRASES.length)
      }
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [
    isFocused,
    searchQuery,
    isProcessing,
    welcomeStagedFile,
    welcomePostMediaChoicePending,
    welcomeUsesContextPending,
    welcomeUsesContextPost,
    isHoveringSubtitle,
    subtitlePhraseIndex,
    subtitleDisplayText,
    isDeletingSubtitle
  ])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour >= 6 && hour < 12) return "Buenos días"
    if (hour >= 12 && hour < 20) return "Buenas tardes"
    return "Buenas noches"
  }

  const normalizedQuery = normalizeText(searchQuery).trim()
  const hasTypedQuery = normalizedQuery.length > 0
  const canSubmitWelcomeSearch =
    (hasTypedQuery ||
      welcomeStagedFile != null ||
      welcomeUsesContextPending ||
      welcomeUsesContextPost) &&
    !welcomePdfChoicePending &&
    !welcomePostMediaChoicePending &&
    !composerDocumentChoicePending &&
    !composerPostMediaChoicePending

  const handleClearSearch = () => {
    setSearchQuery('')
    clearWelcomeStagedAttachment()
    if (welcomeUsesContextPending || welcomeUsesContextPost) {
      clearPendingImage()
      setActiveAgent(AGENT_NAMES.general)
      setCollectedData(null)
    }
    setMatchedSuggestion(null)
    setIsProcessing(false)
    onAgentStatusChange?.(false)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmitWelcomeSearch || isProcessing || contextIsProcessing) {
      return
    }

    let userQuery = searchQuery.trim()
    const staged = welcomeStagedFile
    setIsProcessing(true)
    onAgentStatusChange?.(true)
    if (isMobile) {
      if (showMobileSuggestions) {
        setShowMobileSuggestions(false)
      }
      onMobileResultsChange?.({ status: 'loading' })
    }
    setMatchedSuggestion(null)

    const reuseWelcomePreparedAutofill =
      !staged &&
      !isConversationalModeActive &&
      hasPendingImage &&
      activeAgent === AGENT_NAMES.projectAutofillAgent &&
      collectedData?.intent === 'project_autofill' &&
      currentSession != null

    const reuseWelcomePreparedPost =
      !staged &&
      !isConversationalModeActive &&
      hasPendingImage &&
      activeAgent === AGENT_NAMES.postAgent &&
      (collectedData?.intent === 'create_post' ||
        collectedData?.intent === 'create_portfolio_item') &&
      currentSession != null

    if (reuseWelcomePreparedAutofill) {
      activateConversationalMode()
      if (!userQuery) {
        userQuery = pendingAttachmentFallbackCaption(pendingAttachmentMime ?? null)
      }
    } else if (reuseWelcomePreparedPost) {
      activateConversationalMode()
      if (!userQuery) {
        userQuery = pendingAttachmentFallbackCaption(pendingAttachmentMime ?? null)
      }
    } else {
      const newSession = createSession()
      if (staged) {
        beginComposerPreserveForSession(newSession.sessionId)
      }
      if (!isConversationalModeActive) {
        activateConversationalMode()
      }

      if (staged) {
        try {
          const payload =
            welcomeStagedExtraPdfFiles.length > 0 ? [staged, ...welcomeStagedExtraPdfFiles] : staged
          await storePendingImage(payload, { sessionAnchor: newSession.sessionId })
        } finally {
          endComposerPreserveForSession()
        }
        clearWelcomeStagedAttachment()
        if (!userQuery) {
          userQuery = pendingAttachmentFallbackCaption(staged.type || null)
        }
      }
    }

    const suggestionPool: (DesktopSuggestion | MobileResponse)[] = isMobile ? mobileResponses : desktopSuggestions
    let suggestionMatch: DesktopSuggestion | MobileResponse | undefined = undefined
    let assistantResponse: string | null = null
    let llmResultConfidence = 0
    let llmResultActionId: string | null = null

    try {
      const llmResult = await sendMessage(userQuery)
      assistantResponse = llmResult?.response || null
      llmResultConfidence = llmResult?.confidence || 0
      llmResultActionId = llmResult?.actionId || null

      if (llmResult?.actionId) {
        const llmMatch = suggestionPool.find(config => config.id === llmResult.actionId)
        if (llmMatch && llmResult.confidence > 0.5) {
          suggestionMatch = llmMatch
        }
      }
    } catch (error) {
      console.warn('Error al consultar LLM:', error)
    }

    if (suggestionMatch) {
      if (llmResultActionId === 'postularme-ofertas' && llmResultConfidence > 0.6) {
        setMatchedSuggestion(suggestionMatch as DesktopSuggestion)
      } else if (llmResultConfidence > 0.7) {
        setMatchedSuggestion(suggestionMatch as DesktopSuggestion)
      } else {
        setMatchedSuggestion((suggestionMatch as DesktopSuggestion) ?? null)
      }
    } else {
      setMatchedSuggestion(null)
    }
    setIsProcessing(false)
    onAgentStatusChange?.(false)
    if (isMobile) {
      if (suggestionMatch) {
        onMobileResultsChange?.({
          status: 'result',
          label: suggestionMatch.label,
          summary: assistantResponse || (suggestionMatch as MobileResponse).summary,
          cta: suggestionMatch.action
        })
      } else {
        onMobileResultsChange?.({ status: 'empty' })
      }
    }

    setSearchQuery('')
  }

  const handleSendMessage = async (message: string, options?: { confirmExecution?: boolean }) => {
    if (!canSendMessage && !options?.confirmExecution) {
      return
    }

    await sendMessage(message, options)
  }

  useEffect(() => {
    onConversationalModeChange?.(isConversationalModeActive)
  }, [isConversationalModeActive, onConversationalModeChange])

  const handleCloseConversationalMode = useCallback(() => {
    deactivateConversationalMode()
    clearCurrentSession()
    clearWelcomeStagedAttachment()
  }, [deactivateConversationalMode, clearCurrentSession, clearWelcomeStagedAttachment])

  const handleCreateNewSession = useCallback(() => {
    createSession()
    activateConversationalMode()
  }, [createSession, activateConversationalMode])

  const handleSessionShortcut = useCallback(
    async (message: string) => {
      const session = createSession()
      activateConversationalMode()
      await sendMessage(message, { targetSessionId: session.sessionId })
    },
    [createSession, activateConversationalMode, sendMessage]
  )

  useEffect(() => {
    const raw = location.state as { conversationalShortcutMessage?: string } | null | undefined
    const msg =
      typeof raw?.conversationalShortcutMessage === 'string' ? raw.conversationalShortcutMessage.trim() : ''
    if (!msg) {
      return
    }
    if (conversationalShortcutHandledKeyRef.current === location.key) {
      return
    }
    conversationalShortcutHandledKeyRef.current = location.key
    navigate(location.pathname, { replace: true, state: {} })
    void handleSessionShortcut(msg)
  }, [location.state, location.key, location.pathname, navigate, handleSessionShortcut])

  const onConversationalActionsReadyRef = useRef(onConversationalActionsReady)

  const handleOpenHistoryModal = useCallback(() => {
    openChatHistoryModal()
  }, [openChatHistoryModal])

  useEffect(() => {
    onConversationalActionsReadyRef.current = onConversationalActionsReady
  }, [onConversationalActionsReady])

  useEffect(() => {
    if (onConversationalActionsReadyRef.current) {
      const hasActiveConversation = currentSession && currentSession.messages.length > 0
      const actions = {
        onCreateNewSession: handleCreateNewSession,
        onOpenSearchModal: handleOpenHistoryModal,
        onCloseConversationalMode: handleCloseConversationalMode,
        hasActiveConversation: hasActiveConversation,
        totalChatsCount: allSessions.length
      }
      onConversationalActionsReadyRef.current(actions)
    }
  }, [isConversationalModeActive, handleCreateNewSession, handleCloseConversationalMode, handleOpenHistoryModal, currentSession, allSessions.length])

  const handleSuggestionAction = () => {
    if (!matchedSuggestion) {
      return
    }

    matchedSuggestion.action()
    setSearchQuery('')
    setMatchedSuggestion(null)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  const handleSendMessageRef = useRef(handleSendMessage)
  handleSendMessageRef.current = handleSendMessage

  const handleSubtitleClick = useCallback(() => {
    if (isProcessing) return
    const phraseIndex = subtitlePhraseIndex % SUBTITLE_PHRASES.length
    const phrase = phraseIndex === 0 ? '¿Qué puedo hacer en Edify?' : SUBTITLE_PHRASES[phraseIndex]
    if (!isConversationalModeActive) {
      createSession()
      activateConversationalMode()
    }
    if (isMobile) {
      if (showMobileSuggestions) {
        setShowMobileSuggestions(false)
      }
      onMobileResultsChange?.({ status: 'loading' })
    }
    handleSendMessageRef.current(phrase)
  }, [isProcessing, subtitlePhraseIndex, isConversationalModeActive, createSession, activateConversationalMode, isMobile, showMobileSuggestions, onMobileResultsChange])

  if (isConversationalModeActive) {
    return (
      <div className={`${styles.conversationalModeContainer} ${isMobile ? styles.conversationalModeContainerMobile : ''}`}>
        <ConversationalMode
          isActive={isConversationalModeActive}
          isMobile={isMobile}
          messages={currentSession?.messages || []}
          onSendMessage={async (msg, opts) => {
            await sendMessage(msg, opts)
          }}
          onClose={handleCloseConversationalMode}
          isLoading={contextIsProcessing}
          showDocumentPipelineLoop={documentPipelineLoopActive}
          documentPipelinePhase={documentPipelinePhase}
          documentPipelineStreamHint={documentPipelineStreamHint}
          documentPipelinePrimaryFileOriginalName={documentPipelinePrimaryFileOriginalName}
          documentPipelinePrimaryFileSizeBytes={documentPipelinePrimaryFileSizeBytes}
          canSendMessage={canSendMessage}
          remainingMessages={remainingMessages}
          hasReachedLimit={hasReachedLimit}
          limit={limit}
          matchedSuggestion={matchedSuggestion}
          onSuggestionAction={handleSuggestionAction}
          onRequestGuide={() => sendMessage('Ayudame a hacerlo')}
          allSessions={allSessions}
          currentSessionId={currentSession?.sessionId || null}
          onSelectSession={(sessionId) => {
            void loadSession(sessionId).then((session) => {
              if (session) {
                activateConversationalMode()
              }
            })
          }}
          onCreateNewSession={() => {
            createSession()
            activateConversationalMode()
          }}
          onSessionShortcut={handleSessionShortcut}
          onDeleteSession={removeSession}
          userId={userId}
          userData={userData}
          onSidebarStateChange={onSidebarStateChange}
          missingFields={missingFields}
          readyToExecute={readyToExecute}
          showImageUpload={
            activeAgent === AGENT_NAMES.general ||
            activeAgent === AGENT_NAMES.projectAutofillAgent ||
            collectedData?.intent === 'project_autofill' ||
            missingFields.includes('image_uploaded') ||
            (readyToExecute && collectedData?.intent === 'create_post')
          }
          allowDocuments={
            activeAgent === AGENT_NAMES.general ||
            activeAgent === AGENT_NAMES.projectAutofillAgent ||
            collectedData?.intent === 'project_autofill'
          }
          composerDocumentChoicePending={composerDocumentChoicePending}
          onConfirmPendingDocumentPreload={confirmPendingDocumentAsProjectPreload}
          searchDraftHandoff={searchDraftHandoff}
          onSearchDraftHandoffConsumed={consumeSearchDraftHandoff}
          collectedData={collectedData}
          hasPendingImage={hasPendingImage}
          imageUploadAcknowledged={imageUploadAcknowledged}
          pendingImagePreview={pendingImagePreview}
          pendingAttachmentMime={pendingAttachmentMime}
          pendingAttachmentFileName={pendingAttachmentFileName}
          pendingDocumentationFileCount={pendingDocumentationFileCount}
          pendingDocumentationNamesSummary={pendingDocumentationNamesSummary}
          pendingAttachmentSizeBytes={pendingAttachmentSizeBytes}
          lastAnalyzedDocumentationFileName={lastAnalyzedDocumentationFileName}
          onImageSelect={storePendingImage}
          onImageClear={clearPendingImage}
          onConfirmExecution={() => sendMessage('', { confirmExecution: true })}
        />
      </div>
    )
  }

  return (
    <div className={styles.initialModeContainer}>
      <div className={styles.header}>
        <div className={styles.topRow}>
          <div className={styles.greetingSection}>
            <h1 className={styles.greeting}>
              {getGreeting()}, <span className={styles.userName}>{userName}</span>
            </h1>
            <button
              type="button"
              className={`${styles.subtitle} ${styles.subtitleClickable}`}
              onClick={handleSubtitleClick}
              onMouseEnter={() => setIsHoveringSubtitle(true)}
              onMouseLeave={() => setIsHoveringSubtitle(false)}
              disabled={isProcessing}
            >
              {subtitleDisplayText}
            </button>
          </div>
        </div>

      </div>

      {/* {isMobile && mobileActionsPlaceholder} */}
      {isMobile && (
        <div
          className={`${styles.mobileSuggestions} ${showMobileSuggestions ? styles.mobileSuggestionsVisible : ''}`}
        >
          <div
            className={`${styles.mobileSuggestionsViewport} ${showLeftFade ? styles.mobileSuggestionsViewportLeftFade : ''}`}
          >
            <div className={styles.mobileSuggestionList} ref={mobileSuggestionsRef}>
              {mobileQuickSuggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className={`${styles.mobileSuggestionCard} ${
                    suggestion.id === ANALIZAR_DOCS_MOBILE_SUGGESTION_ID ? styles.mobileSuggestionCardNew : ''
                  }`}
                  onClick={() => suggestion.action()}
                >
                  {suggestion.label}
                  <Plus size={16} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className={styles.searchSection}>
        <form ref={welcomeFormRef} className={styles.searchForm} onSubmit={handleSubmit}>
          {welcomeAttachmentError ? (
            <p className={styles.welcomeAttachmentError} role="alert">
              {welcomeAttachmentError}
            </p>
          ) : null}
          <div
            ref={(el) => registerTutorialRef('welcome-ai-input', el)}
            data-tutorial="welcome-ai-input"
            className={`${styles.searchBox} ${isFocused ? styles.searchBoxFocused : ''}`}
          >
            <ConversationInput
              variant="welcome"
              welcomeTextareaClassName={styles.searchInput}
              welcomeSendClassName={styles.sendIconButton}
              welcomeActionsClassName={styles.actionButtons}
              onWelcomeReset={handleClearSearch}
              value={searchQuery}
              onChange={setSearchQuery}
              onSend={() => welcomeFormRef.current?.requestSubmit()}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  welcomeFormRef.current?.requestSubmit()
                }
              }}
              onInputFocus={() => setIsFocused(true)}
              onInputBlur={() => setTimeout(() => setIsFocused(false), 200)}
              disabled={isProcessing || contextIsProcessing}
              placeholder="Contanos qué querés hacer..."
              showImageUpload={welcomeShowAttachment}
              allowVideo={Boolean(welcomeAllowVideo)}
              allowDocuments={welcomeAllowDocuments}
              highlightProjectAutofillAttachment={false}
              hasPendingImage={
                welcomeStagedFile != null || welcomeUsesContextPending || welcomeUsesContextPost
              }
              imageUploadAcknowledged={false}
              pendingImagePreview={
                welcomeStagedFile != null ? welcomePreviewUrl : pendingImagePreview
              }
              pendingAttachmentMime={welcomeStagedFile?.type ?? pendingAttachmentMime}
              pendingAttachmentFileName={welcomePendingAttachmentFileName}
              pendingDocumentationFileCount={
                welcomeStagedFile != null
                  ? welcomePendingDocumentationCount
                  : welcomeUsesContextPending || welcomeUsesContextPost
                    ? pendingDocumentationFileCount
                    : 0
              }
              pendingDocumentationNamesSummary={
                welcomeStagedFile != null
                  ? welcomePendingDocumentationNamesSummary
                  : welcomeUsesContextPending || welcomeUsesContextPost
                    ? pendingDocumentationNamesSummary
                    : null
              }
              pendingAttachmentSizeBytes={welcomePendingAttachmentSizeBytes}
              lastAnalyzedDocumentationFileName={lastAnalyzedDocumentationFileName}
              onImageSelect={handleWelcomeAttachment}
              onImageClear={
                welcomeUsesContextPending || welcomeUsesContextPost
                  ? handleClearSearch
                  : clearWelcomeStagedAttachment
              }
              documentIntentChoicePending={welcomePdfChoicePending}
              onConfirmDocumentAsProjectPreload={handleWelcomeConfirmPreload}
              postMediaIntentChoicePending={welcomePostMediaChoicePending}
              onConfirmPostMediaRoute={handleWelcomeConfirmPostRoute}
              showProjectAutofillSendHint={showProjectAutofillSendHint}
              showPostMediaSendHint={showPostMediaSendHint}
              onOpenHistory={isMobile && historyCount > 0 ? () => openChatHistoryModal() : undefined}
              historyCount={isMobile ? historyCount : undefined}
            />
          </div>
        </form>
      </div>

    </div>
  )
}

export default DashboardHeader

